import { Icon, ProfileName } from 'components';
import { CREATE_EVENT } from 'constants/sidebars';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { initialFiltersState } from 'sidebars/CreateEventOverall/utils';
import { openModal } from 'store/actionCreators/modal';
import {
  Community,
  CommunityUser,
  Event,
  EventGridItem,
  EventReply,
  EventResponse,
  EventTask,
  EventToRender,
  GroupUser,
  User,
} from 'types';
import { getProfileName } from 'utils';

import {
  getTaskValues,
  getUserValues,
} from '../../CommunityOverallScheduler/Schedule/values';

export const useGridData = ({
  tasks,
  adminMembers,
  groupLeads,
  members,
  eventsToRender,
  responses,
  openGiveReplySidebar,
  openTaskDetailsSidebar,
  currentUser,
  currentUserInGroup,
  zoom,
  openViewMemberSidebar,
  community,
  searchFilter,
  groupId,
  events,
}: {
  searchFilter: string;
  tasks: EventTask[];
  adminMembers: GroupUser[];
  groupLeads: GroupUser[];
  members: GroupUser[];
  eventsToRender: EventToRender[];
  responses: EventResponse[];
  openGiveReplySidebar: (props: {
    id: string;
    eventId: string;
    eventDate: Date;
    subReply?: EventReply;
    subTaskReplies?: string[];
    isViewOnly: boolean;
    isAdmin: boolean;
    name: string;
  }) => void;
  openTaskDetailsSidebar: (task: EventTask, userIds: string[]) => void;
  currentUser: CommunityUser;
  currentUserInGroup: any;
  zoom: number;
  openViewMemberSidebar: (user: User) => void;
  community: Community;
  groupId: string;
  events: Event[];
}): EventGridItem[] => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const openEditEventSidebar = useCallback(
    (eventId: string, date: string) => {
      const originalEvent = events?.find((e) => e.id === eventId);
      if (!originalEvent) return;

      dispatch(
        openModal({
          type: CREATE_EVENT,
          placement: 'right',
          data: {
            groupId: groupId,
            event: originalEvent,
            date: new Date(date),
            filterFrom: initialFiltersState().from,
            filterTo: initialFiltersState().to,
            openTasksTab: true,
          },
        }),
      );
    },
    [events, groupId, dispatch],
  );

  const tasksData = useMemo<EventGridItem[]>(() => {
    return Array.isArray(tasks)
      ? [
          {
            type: 'group',
            id: 'tasks',
            label: t('common.tasks'),
          },
          ...tasks.map((task) => ({
            type: 'task',
            id: task.id,
            group: 'tasks',
            label: task.title,
            values: getTaskValues(
              task,
              eventsToRender,
              openTaskDetailsSidebar,
              openEditEventSidebar,
              currentUser,
              community,
            ),
            icon: <Icon icon={task.icon} />,
          })),
        ]
      : [];
  }, [
    tasks,
    eventsToRender,
    openTaskDetailsSidebar,
    openEditEventSidebar,
    currentUser,
    community,
  ]);

  const getUserData = (user: GroupUser, groupName: string) => ({
    type: 'user',
    id: user.profile.userId,
    group: groupName,
    label: (
      <ProfileName
        searchFilter={searchFilter}
        avatar={user.profile.smallLogo || user.profile.logo}
        name={getProfileName(user.profile)}
        communityPermission={user.communityPermission}
        groupPermission={user.groupPermission}
        size="sm"
        zoom={zoom}
        onClick={() => {
          openViewMemberSidebar(user);
        }}
      />
    ),
    values: getUserValues({
      userId: user.profile.userId,
      responses,
      eventsToRender,
      openGiveReplySidebar,
      currentUser,
      currentUserInGroup,
      name: getProfileName(user.profile),
      community,
    }),
    onClick: () => openViewMemberSidebar(user),
  });

  const groupsData = useMemo<EventGridItem[]>(() => {
    return [
      {
        type: 'group',
        id: 'admin',
        label: t('common.admin'),
      },
      ...adminMembers.map((member) => getUserData(member, 'admin')),
      {
        type: 'group',
        id: 'groupLead',
        label: t('common.group_lead'),
      },
      ...groupLeads.map((member) => getUserData(member, 'groupLead')),
      {
        type: 'group',
        id: 'member',
        label: t('common.member'),
      },
      ...members.map((member) => getUserData(member, 'member')),
    ];
  }, [eventsToRender, adminMembers, groupLeads, members, zoom]);

  return useMemo(() => {
    return [...tasksData, ...groupsData];
  }, [tasksData, groupsData]);
};
